import * as S from './styled'
import { Eval } from '@app/evaluation-core/types'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import React, { useState, useEffect, FC, useCallback } from 'react'
import { useSnackbar } from '@app/snackbar'
import useUser from '@common/hooks/useUser'
import {
  updateClubName,
  updateCourseName,
} from '@app/evaluation-core/actions/evaluationActions'
import useEvaluationUser from '@common/hooks/useEvaluationUser'
import DateInput from '../DatePicker/DatePicker'
import fb from '@app/firebase'
import {
  updateEvaluationComment,
  updateEvaluationDate,
  updateEvaluationDesciption,
  updateEvaluationManualVersion,
  updateEvaluationRatingRevision,
} from '@app/evaluation-core/evaluation'
import { EvalStatus } from '@app/courses/domain/ratingStatus'
import CourseScorecardModal from '../CourseScorecardModal/CourseScorecardModal'
import { LRClub } from '../../../types/Clubs'
import { SelectField, TextField } from '../FormFields'
import firebase from 'firebase'
import TeeColorPicker from '../TeeColorPicker'
import { MANUAL_VERSIONS, ManualVersion } from '../../../versioning'
import SlopeTableHeaderPreview from '../SlopeTableHeaderPreview/SlopeTableHeaderPreview'
import { formatManualVersion } from '@common/utils/formatManualVersion'

interface CourseInfoProps {
  club?: LRClub
  evaluation: Eval
  gcId?: string
  originalClubName?: string
}

const CourseInfo: FC<CourseInfoProps> = ({ evaluation, originalClubName }) => {
  const user = useUser()
  const evalUser = useEvaluationUser()
  const [openSnackbar] = useSnackbar()

  const handleSaveField = async (field: string, value: any) => {
    if (evaluation.id) {
      const updateActions: { [key: string]: () => Promise<void> } = {
        clubName: () =>
          updateClubName(value.toUpperCase(), evaluation.id as string),
        courseName: () => updateCourseName(value, evaluation.id as string),
        saveName: () =>
          fb.ref(`evaluations/${evaluation.id}/saveName`).set(value),
        evaluationDate: () =>
          updateEvaluationDate(evaluation.id as string, value),
        comment: () => updateEvaluationComment(evaluation.id as string, value),
        description: () => updateEvaluationDesciption(value),
        manualVersion: () => updateEvaluationManualVersion(value),
        ratingRevision: () => updateEvaluationRatingRevision(Number(value)),
      }
      await updateActions[field]?.()
    }
  }

  const copyToClipboard = async () => {
    if (evaluation.id) {
      await navigator.clipboard.writeText(evaluation.id)
      openSnackbar('Rating ID copied to clipboard.')
    }
  }

  const canEdit =
    evaluation?.status !== EvalStatus.FINALIZED &&
    (user.isAdmin ||
      Object.values(evaluation?.users).some((id) => +id.userId == user.id))
  const canEditFinalized =
    evaluation?.status === EvalStatus.FINALIZED &&
    (user.isAdmin ||
      Object.values(evaluation?.users).some((id) => +id.userId == user.id))

  const [scorecardModalVisible, setScorecardModalVisible] = useState(false)

  const onSaveTeeOptions = useCallback(
    async (teeColors: string[], teeNames: string[]) => {
      console.log({ teeColors, teeNames })
      for (let i = 0; i < teeColors.length; i++) {
        if (teeColors[i].startsWith('#')) {
          teeColors[i] = teeColors[i].substring(1)
        }

        if (
          teeColors[i] === '' ||
          teeColors[i] === undefined ||
          teeColors[i] === null ||
          teeColors[i].length !== 6
        ) {
          openSnackbar('Invalid color value found')
          return
        }
      }

      for (let i = 0; i < teeNames.length; i++) {
        if (
          teeNames[i] === '' ||
          teeNames[i] === undefined ||
          teeNames[i] === null
        ) {
          openSnackbar('Invalid tee name found')
          return
        }
      }

      try {
        const updates = {}
        const snapshot = await firebase
          .database()
          .ref(`evaluations/${evaluation.id}/holes`)
          .get()

        if (snapshot.exists()) {
          snapshot.forEach((holeSnapshot) => {
            const holeNumber = holeSnapshot.key
            teeColors.forEach((newColor, teeIndex) => {
              updates[
                `evaluations/${evaluation.id}/holes/${holeNumber}/tees/${teeIndex}/color`
              ] = newColor
            })

            teeNames.forEach((newName, teeIndex) => {
              updates[
                `evaluations/${evaluation.id}/holes/${holeNumber}/tees/${teeIndex}/name`
              ] = newName
            })
          })

          console.log('updates', updates)

          await firebase.database().ref().update(updates)
          openSnackbar('Tee Info updated successfully')
        }
      } catch (error) {
        console.error('Error updating tee colors:', error)
        openSnackbar('Error updating tee info')
      }
    },
    [evaluation, openSnackbar]
  )

  if (!evaluation) return <></>

  return (
    <>
      <CourseScorecardModal
        courseId={Number(evaluation.courseId)}
        isVisible={scorecardModalVisible}
        closeFn={() => setScorecardModalVisible(false)}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <S.ContentHeading>
            {evaluation?.clubName}, {evaluation?.courseName}
          </S.ContentHeading>
          <AdminControls
            user={user}
            evaluation={evaluation}
            copyToClipboard={copyToClipboard}
          />
        </div>
        <SlopeTableHeaderPreview
          clubName={evaluation?.clubName ?? undefined}
          courseName={evaluation?.courseName ?? undefined}
          ratingName={evaluation?.saveName ?? undefined}
          holeCount={evaluation?.holes?.length}
          totalPar={evaluation?.holes?.reduce(
            (sum, h) => sum + Number(h.par),
            0
          )}
          revision={evaluation?.ratingRevision?.ratingRevision}
          manualVersion={evaluation?.manualVersion}
          ratingDate={
            evaluation?.evaluationDate ??
            evaluation?.ratingRevision?.ratingDate ??
            undefined
          }
        />
      </div>

      <div>
        <TextField
          label="Club name"
          defaultValue={evaluation?.clubName as string}
          onBlur={(value) => handleSaveField('clubName', value)}
          disabled={!canEdit && !canEditFinalized}
          canReset={true}
          resetValue={originalClubName?.toUpperCase()}
          uppercase={true}
        />
      </div>

      <TextField
        label="Course name"
        defaultValue={evaluation?.courseName as string}
        onBlur={(value) => handleSaveField('courseName', value)}
        disabled={!canEdit && !canEditFinalized}
        canReset={true}
        resetValue={evaluation.holes?.[0]?.courseName}
      />
      <TextField
        label="Rating Name"
        additionalText="This text will appear in the Slope Tables."
        defaultValue={evaluation?.saveName as string}
        onBlur={(value) => handleSaveField('saveName', value)}
        disabled={!canEdit && !canEditFinalized}
      />
      <DateField
        label="Rating Date"
        additionalText="This date will appear in the FINALIZED Slope Tables."
        defaultValue={evaluation?.evaluationDate as string}
        onChange={(date) => handleSaveField('evaluationDate', date)}
        disabled={!canEdit && !canEditFinalized}
      />
      <TextField
        label="Rating Revision"
        additionalText="Optional. Specify a revision number for the rating."
        defaultValue={
          evaluation?.ratingRevision
            ? String(evaluation?.ratingRevision?.ratingRevision)
            : ''
        }
        onBlur={(value) => handleSaveField('ratingRevision', Number(value))}
        disabled={!canEdit && !canEditFinalized}
        isInputWithButton={false}
        buttonTitle="Save"
      />

      <SelectField
        defaultValue={(evaluation?.manualVersion as string) ?? ''}
        additionalText="The years version of the manual that this rating was evaluated with."
        inputWidth="306px"
        label="Manual Version"
        onChange={(value) => handleSaveField('manualVersion', value)}
        options={MANUAL_VERSIONS.map((version) => ({
          value: version,
          label: formatManualVersion(version),
        }))}
        disabled={!canEdit}
      />

      <TextArea
        label="Rating Description"
        defaultValue={evaluation?.description as string}
        onBlur={(value) => handleSaveField('description', value)}
        disabled={!canEdit && !canEditFinalized}
      />
      <TextArea
        label="Rating Comments"
        defaultValue={evaluation?.comment as string}
        onBlur={(value) => handleSaveField('comment', value)}
        disabled={!canEdit && !canEditFinalized}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>Rating Tee Colors and Names</span>
        <TeeColorPicker
          tees={evaluation?.holes.at(0)?.tees}
          onSave={onSaveTeeOptions}
        />
      </div>
    </>
  )
}

interface AdminControlsProps {
  user: ReturnType<typeof useUser>
  evaluation: Eval
  copyToClipboard: () => Promise<void>
}

const AdminControls: FC<AdminControlsProps> = ({
  user,
  evaluation,
  copyToClipboard,
}) => {
  if (!user.isAdmin) return null
  return (
    <div style={{ margin: 12, display: 'flex' }}>
      <div
        onClick={copyToClipboard}
        style={{ cursor: 'pointer', marginRight: 6 }}
      >
        <FeatherIcon name="clipboard" size={18} />
      </div>
      <span>{`Rating ID: ${evaluation?.id}`}</span>
    </div>
  )
}

interface DateFieldProps {
  label: string
  additionalText: string
  defaultValue: string
  onChange: (date: Date) => void
  disabled: boolean
}

const DateField: FC<DateFieldProps> = ({
  label,
  additionalText,
  defaultValue,
  onChange,
  disabled,
}) => {
  const [date, setDate] = useState(new Date(defaultValue || new Date()))

  useEffect(() => {
    onChange(date)
  }, [date])

  return (
    <S.CourseOptionView inputWidth="300px">
      <div>
        <label>
          <span style={{ fontWeight: 400 }}>{label}</span>
          {additionalText && <span>{additionalText}</span>}
        </label>
        <div>
          <DateInput stateHook={[date, setDate]} disabled={disabled} />
        </div>
      </div>
    </S.CourseOptionView>
  )
}

interface TextAreaProps {
  label: string
  defaultValue: string
  onBlur: (value: string) => void
  disabled: boolean
}

const TextArea: FC<TextAreaProps> = ({
  label,
  defaultValue,
  onBlur,
  disabled,
}) => {
  const [value, setValue] = useState(defaultValue)

  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  return (
    <S.CourseOptionView>
      <div>
        <span>{label}</span>
        <textarea
          style={{
            minWidth: 288,
            minHeight: 72,
            resize: 'none',
            padding: 8,
            lineHeight: '25px',
          }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => onBlur(value)}
          disabled={disabled}
        />
      </div>
    </S.CourseOptionView>
  )
}

export default CourseInfo
